// src/components/SlideDeckPreview.tsx
//
// A lightweight in-chat preview of what the generated .pptx will look
// like — NOT a literal render of the actual file (that would need a real
// PowerPoint renderer, which isn't available on shared hosting), but an
// HTML/CSS approximation built from the exact same JSON spec that drives
// api/generate_file.php, so it's a faithful preview of colors, layout, and
// content. Chart slides reuse the same ChartRenderer from Phase 2.
import React, { useState } from 'react';
import ChartRenderer from './ChartRenderer';

interface Theme {
  primary: string;
  accent: string;
  background: string;
  text: string;
}

interface Slide {
  layout?: 'title' | 'content' | 'chart' | 'quote' | 'closing';
  title?: string;
  subtitle?: string;
  bullets?: string[];
  chart?: any;
}

interface Props {
  title?: string;
  theme?: Partial<Theme>;
  slides: Slide[];
}

const DEFAULT_THEME: Theme = {
  primary: '#1F4E79',
  accent: '#2E86AB',
  background: '#FFFFFF',
  text: '#1A1A1A',
};

function hex(color?: string): string {
  if (!color) return '';
  return color.startsWith('#') ? color : `#${color}`;
}

function inferLayout(slide: Slide): NonNullable<Slide['layout']> {
  if (slide.layout) return slide.layout;
  if (slide.chart) return 'chart';
  if (slide.bullets && slide.bullets.length) return 'content';
  return 'quote';
}

export default function SlideDeckPreview({ title, theme, slides }: Props) {
  const [idx, setIdx] = useState(0);
  const t: Theme = {
    primary: hex(theme?.primary) || DEFAULT_THEME.primary,
    accent: hex(theme?.accent) || DEFAULT_THEME.accent,
    background: hex(theme?.background) || DEFAULT_THEME.background,
    text: hex(theme?.text) || DEFAULT_THEME.text,
  };

  if (!slides || slides.length === 0) return null;
  const slide = slides[Math.min(idx, slides.length - 1)];
  const layout = inferLayout(slide);

  const goPrev = () => setIdx(i => Math.max(0, i - 1));
  const goNext = () => setIdx(i => Math.min(slides.length - 1, i + 1));

  const isDarkLayout = layout === 'title' || layout === 'closing';
  const isAccentLayout = layout === 'quote';
  const slideBg = isDarkLayout ? t.primary : (isAccentLayout ? t.accent : t.background);
  const textColor = (isDarkLayout || isAccentLayout) ? '#FFFFFF' : t.text;

  return (
    <div className="slide-deck-preview">
      <div className="slide-deck-stage" style={{ background: slideBg }}>
        {(layout === 'content' || layout === 'chart') && (
          <div className="slide-accent-bar" style={{ background: t.accent }} />
        )}

        {layout === 'title' && (
          <div className="slide-center">
            <p className="slide-title-big" style={{ color: textColor }}>{slide.title}</p>
            {slide.subtitle && <p className="slide-subtitle" style={{ color: textColor }}>{slide.subtitle}</p>}
          </div>
        )}

        {layout === 'closing' && (
          <div className="slide-center">
            <p className="slide-title-big" style={{ color: textColor }}>{slide.title || 'Thank You'}</p>
            {slide.subtitle && <p className="slide-subtitle" style={{ color: textColor }}>{slide.subtitle}</p>}
          </div>
        )}

        {layout === 'quote' && (
          <div className="slide-center">
            <p className="slide-quote" style={{ color: textColor }}>{slide.title}</p>
            {slide.subtitle && <p className="slide-subtitle" style={{ color: textColor }}>{slide.subtitle}</p>}
          </div>
        )}

        {layout === 'content' && (
          <div className="slide-content">
            <p className="slide-title-small" style={{ color: t.primary }}>{slide.title}</p>
            <ul className="slide-bullets" style={{ color: t.text }}>
              {(slide.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}

        {layout === 'chart' && (
          <div className="slide-content">
            <p className="slide-title-small" style={{ color: t.primary }}>{slide.title}</p>
            {slide.chart ? (
              <div className="slide-chart-wrap">
                <ChartRenderer code={JSON.stringify(slide.chart)} />
              </div>
            ) : (
              <p className="slide-subtitle" style={{ color: t.text }}>(no chart data)</p>
            )}
          </div>
        )}
      </div>

      <div className="slide-deck-nav">
        <button className="code-block-btn" onClick={goPrev} disabled={idx === 0}>‹ Prev</button>
        <span className="slide-deck-counter">{idx + 1} / {slides.length}</span>
        <button className="code-block-btn" onClick={goNext} disabled={idx === slides.length - 1}>Next ›</button>
      </div>
    </div>
  );
}